import { memo } from 'react';
import Headroom from 'react-headroom';
import Link from 'next/link';
import cn from 'classnames';

import Button from '@/components/Button';
import MainNav from '@/components/nav/main-nav';
import LogoSVG from '@/svg/logo.svg';
import MenuToggle from '@/components/MenuToggle';
import {
  useNavigationState,
  useNavigationDispatch,
} from '@/contexts/navigation';

type StickyNavProps = {
  handleIsFixed: (isFixed: boolean) => void;
  windowWidth: number | null;
};

function StickyNav({ handleIsFixed, windowWidth }: StickyNavProps) {
  const { isOpen } = useNavigationState();
  const { toggleNav } = useNavigationDispatch();

  return (
    <Headroom
      pinStart={60}
      onUnfix={() => handleIsFixed(true)}
      onPin={() => handleIsFixed(false)}
      onUnpin={() => handleIsFixed(false)}
      style={isOpen ? { boxShadow: `none` } : {}}
    >
      <div
        className={cn(
          `relative mx-auto flex max-w-6xl flex-col py-4 leading-relaxed lg:flex-row lg:justify-between lg:rounded lg:py-8 lg:px-6`,
          {
            'pb-0': isOpen,
          },
        )}
        style={{
          perspective: `2000px`,
        }}
      >
        <div className="flex w-full flex-row items-center justify-between px-6 lg:w-auto lg:px-0">
          <Link href="/" prefetch={false}>
            <a>
              <span className="sr-only">GraphCMS</span>
              <LogoSVG className="h-[30px] max-w-[132px] fill-current" />
            </a>
          </Link>

          <div className="flex appearance-none flex-row items-center justify-between lg:hidden">
            <MenuToggle className="-mr-3" onClick={toggleNav} open={isOpen} />
          </div>
        </div>

        {windowWidth && windowWidth >= 1024 && (
          <>
            <MainNav isDesktop />

            {/* Desktop */}
            <div className="hidden w-auto items-center justify-evenly whitespace-nowrap px-4 lg:flex lg:flex-row lg:px-0">
              <div>
                <Button
                  colour="faded-cornflower"
                  href="/contact"
                  size="small"
                  className="px-4"
                >
                  Contact sales
                </Button>
              </div>

              <div className="lg:ml-2">
                <Button
                  colour="purple"
                  href="https://app.graphcms.com/signup"
                  size="small"
                  className="px-4"
                  withArrow
                >
                  Get started
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Headroom>
  );
}

export default memo(StickyNav);
